import {
  Activity, BadgeCheck, BarChart3, BellRing, BookOpen, Boxes, Building, Building2, BrainCircuit,
  CircleDollarSign, CircleHelp, ClipboardCheck, ClipboardList, Clock, Compass, CreditCard, FileSearch, FileText, Filter,
  FlaskConical, FolderOpen, Gauge, Globe, Inbox,
  KeyRound, Landmark, Languages, Layers, LayoutDashboard, List, MapPin, Megaphone, MessageSquarePlus, MousePointerClick, Package, PackagePlus, PanelTop,
  PlayCircle, Printer, Radio, Receipt, Rows3, ScanLine, Search, Send, Server, Settings, ShoppingBag, ShoppingCart, Smartphone, Store, Tag,
  TicketPercent, Truck, Upload, UserCircle, Users, Warehouse, Watch, Workflow,
} from 'lucide-react';
import { tx } from './i18n';

// The whole sidebar, as data.
//
// Three levels: a section heading, an item, and — where an item has more than
// one screen behind it — children. A section is only a heading; it carries no
// authorization and no route. Nesting stops at children on purpose: a third
// level means the sidebar has become the product.
//
// PERMISSIONS. Every destination names a permission code that already exists in
// xomics_permission and is already enforced somewhere. Nothing here invents a
// code: a permission that gates a menu item and nothing else is a label, not a
// control, and it would also show up as a notification option nobody can use.
// Screens whose endpoints do not exist yet borrow the nearest real permission,
// listed under `note` so it is obvious which ones need their own code once they
// have something to guard.
//
// `built: false` means the screen is a placeholder. It is in the tree because
// the shape of the product is a decision worth seeing whole, not because it
// works.
//
// `kinds` names the kinds of location whose people see an item: a warehouse
// and a retail shop sell nothing, so selling is an office's, and a warehouse's
// pages are a warehouse's. Whoever may pick any office's store
// (STORE_MANAGE_ALL) sees every kind's pages, wherever they work.

export const NAV = [
  {
    section: null,
    items: [
      {
        path: 'overview', label: "ui.nav.dashboard_d87f47b4", icon: LayoutDashboard,
        permission: null, built: true, keywords: ["ui.nav.home_e83249bd", "ui.nav.start_2b020927", "ui.nav.overview_fed758b7"],
      },
    ],
  },
  {
    section: "ui.nav.clinical_65b7c320",
    items: [
      {
        path: 'projects', label: "ui.nav.projects_53e890d5", icon: FolderOpen,
        permission: 'PROJECT_READ', built: true,
        keywords: ["ui.nav.patient_b1b0b8de", "ui.nav.cohort_1e99e68d", "ui.nav.subproject_8d43139c"],
      },
      {
        path: 'uploads', label: "ui.nav.uploads_227cc640", icon: Upload,
        permission: 'UPLOAD_READ', built: true,
        keywords: ["ui.nav.sample_8151325d", "ui.nav.fastq_29f03445", "ui.nav.submit_3fa4054f", "ui.nav.sequencing_bb25eb49"],
      },
      {
        // The request screen is the first child and keeps the parent's own path
        // too, so every existing link to Reporting still opens it.
        path: 'reporting', label: "ui.nav.reporting_c9e579d6", icon: FlaskConical,
        permission: 'REPORT_READ', built: true,
        keywords: ["ui.nav.report_a27297bd", "ui.nav.request_088e29b0", "ui.nav.pdf_ce9f44bc", "ui.nav.clinical_3a811698"],
        children: [
          {
            path: 'reporting/requests', label: "ui.nav.report_requests", icon: FlaskConical, built: true,
            keywords: ["ui.nav.report_a27297bd", "ui.nav.request_088e29b0", "ui.nav.pdf_ce9f44bc", "ui.nav.clinical_3a811698"],
          },
          {
            path: 'reporting/files', label: "ui.nav.report_files", icon: FolderOpen, built: true,
            keywords: ["ui.nav.report_a27297bd", "ui.nav.report_kw_every_report", "ui.nav.report_kw_search", "ui.nav.report_kw_folder", "ui.nav.pdf_ce9f44bc"],
          },
          {
            path: 'reporting/analytics', label: "ui.nav.analytics_25bc9629", icon: BarChart3, built: true,
            keywords: ["ui.nav.report_a27297bd", "ui.nav.report_kw_volume", "ui.nav.report_kw_generation_time", "ui.nav.report_kw_failures"],
          },
        ],
      },
      {
        path: 'validation', label: "ui.nav.validation_dd74d182", icon: BadgeCheck,
        permission: 'SAMPLE_VALIDATE', built: true,
        keywords: ["ui.nav.clinician_74320583", "ui.nav.sign_off_c5e3ab66", "ui.nav.approve_e4788b8a", "ui.nav.qc_d6426af0"],
      },
    ],
  },
  {
    section: "ui.nav.samples_0fb59422",
    items: [
      {
        path: 'kits', label: "ui.collections.title", icon: Package,
        permission: 'KIT_READ', built: true,
        keywords: ["ui.nav.kit_b17eb192", "ui.nav.collection_2037de43", "ui.nav.tube_0ae2f41c"],
      },
      {
        path: 'sample-tracking', label: "ui.nav.sample_tracking_6cd0bd6b", icon: MapPin,
        permission: 'SAMPLE_TRACK_READ', built: true,
        keywords: ["ui.nav.wetlab_61a74dbc", "ui.nav.extraction_815fe720", "ui.nav.library_00299a40", "ui.nav.freezer_3e835a54"],
      },
    ],
  },
  {
    section: "ui.nav.laboratory_section",
    items: [
      {
        path: 'laboratory/overview', label: "ui.nav.overview_fed758b7", icon: LayoutDashboard,
        permission: 'LAB_INBOX_READ', built: true,
        keywords: ["ui.nav.laboratory_section", "ui.nav.metrics_966b9734"],
      },
      {
        path: 'laboratory/operations', label: "ui.nav.operations", icon: ClipboardList,
        permission: 'LAB_INBOX_READ', built: false,
        keywords: ["ui.nav.sample_8151325d", "ui.nav.workflow_899531e0", "ui.nav.qc_d6426af0"],
        children: [
          {
            path: 'laboratory/receipt', label: "ui.nav.lab_receipt", icon: ScanLine,
            built: true,
            keywords: ["ui.nav.received_by_lab", "ui.nav.lab_acceptance", "ui.nav.lab_scan"],
          },
          {
            path: 'laboratory/customer-submissions', label: "ui.lab.customers.title", icon: Inbox,
            built: true,
            keywords: ["ui.nav.customers", "ui.lab.customers.keyword_qc", "ui.lab.customers.keyword_form"],
          },
          {
            path: 'laboratory/samples', label: "ui.nav.sample_inventory", icon: FlaskConical,
            built: true,
            keywords: ["ui.nav.sample_8151325d", "ui.nav.wetlab_61a74dbc", "ui.nav.received_by_lab"],
          },
          {
            path: 'laboratory/work', label: "ui.lab.work.title", icon: Workflow,
            built: true,
            keywords: ["ui.nav.workflow_899531e0", "ui.nav.pipeline_73979b3d", "ui.lab.work_orders", "ui.lab.sequencing_runs"],
          },
          {
            path: 'laboratory/batch', label: "ui.lab.batch.title", icon: Rows3,
            built: true,
            keywords: ["ui.nav.lab_scan", "ui.nav.workflow_899531e0", "ui.lab.work.title"],
          },
          {
            path: 'laboratory/approvals', label: "ui.lab.approvals", icon: BadgeCheck,
            built: true,
            keywords: ["ui.nav.qc_d6426af0", "ui.nav.validation_dd74d182"],
          },
        ],
      },
      {
        path: 'laboratory/workflows', label: "ui.lab.workflows", icon: Workflow,
        permission: 'LAB_INBOX_READ', built: false,
        keywords: ["ui.lab.flow.title", "ui.lab.analysis.title", "ui.lab.rules.title"],
        children: [
          { path: 'laboratory/workflow-definitions', label: "ui.lab.flow.title", icon: Workflow, built: true,
            keywords: ["ui.lab.stages"] },
          { path: 'laboratory/analyses', label: "ui.lab.analysis.title", icon: BookOpen, built: true,
            keywords: ["ui.nav.lab_analysis_list", "ui.nav.sample_8151325d"] },
          { path: 'laboratory/acceptance-rules', label: "ui.lab.rules.title", icon: ClipboardCheck, built: true,
            keywords: ["ui.nav.lab_acceptance"] },
        ],
      },
      {
        path: 'laboratory/devices', label: "ui.lab.machines.title", icon: Server,
        permission: 'LAB_INBOX_READ', built: true,
        keywords: ["ui.lab.serial_number", "ui.nav.lab_inventory_number"],
      },
    ],
  },
  {
    section: "ui.nav.research_be601df2",
    items: [
      {
        path: 'cohorts', label: "ui.nav.cohorts_c54a6a4e", icon: Users,
        permission: 'COHORT_READ', built: true,
        keywords: ["ui.nav.group_64292b1c", "ui.nav.population_db4d3305", "ui.nav.set_65c10dc3"],
      },
      {
        path: 'omics', label: "ui.nav.omics_44a074b0", icon: FileSearch,
        permission: 'OMICS_QUERY', built: false,
        keywords: ["ui.nav.query_7cd9148e", "ui.nav.explore_9dda9754", "ui.nav.genome_a5c9b737", "ui.nav.transcriptome_83ca3ea9", "ui.nav.variants_0f352806"],
      },
    ],
  },
  {
    section: "ui.nav.logistics_7a2c34cb",
    items: [
      {
        path: 'orders', label: "ui.nav.orders_cded0933", icon: ShoppingCart,
        permission: 'ORDER_READ', built: true,
        keywords: ["ui.nav.purchase_d376ca29", "ui.nav.basket_c9be014f", "ui.nav.checkout_d5491e7e"],
      },
      {
        path: 'shipments', label: "ui.nav.shipments_7a70be8a", icon: Truck,
        permission: 'SHIPMENT_READ', built: true,
        note: "ui.nav.courier_state_as_opposed_to_the_wet_lab_state_on_sampl_286092aa",
        keywords: ["ui.nav.courier_023daeb6", "ui.nav.parcel_58906f65", "ui.nav.delivery_391cb1e0", "ui.nav.tracking_da4dfe3f"],
      },
    ],
  },
  {
    section: "ui.nav.platforms_ac1a42f5",
    items: [
      {
        path: 'webservers', label: "ui.nav.web_a8323a2a", icon: Server,
        permission: 'PLATFORM_WEB_READ', built: true,
        keywords: ["ui.nav.site_c099a42a", "ui.nav.oncorisk_3c2f102c", "ui.nav.polygen_473acdf3", "ui.nav.familyrisk_3fca1ea5", "ui.nav.newgenome_cde02d96"],
        children: [
          { path: 'webservers/overview', label: "ui.nav.overview_fed758b7", icon: LayoutDashboard, built: true, keywords: ["ui.nav.site_c099a42a", "ui.nav.services_b8572d89"] },
          { path: 'webservers/analytics', label: "ui.nav.analytics_25bc9629", icon: Activity, built: true, keywords: ["ui.nav.visits_ec7b4ab4", "ui.nav.sessions_781791f5", "ui.nav.traffic_3d139ce0"] },
        ],
      },
      {
        path: 'mobile', label: "ui.nav.mobile_app_7620a2b9", icon: Smartphone,
        permission: 'PLATFORM_MOBILE_READ', built: true,
        keywords: ["ui.nav.ios_0f8d2573", "ui.nav.android_e4bbe5b7", "ui.nav.phone_f6be6ca9"],
        children: [
          {
            path: 'mobile/overview', label: "ui.nav.mobile_pulse", icon: Radio, built: true,
            keywords: ["ui.nav.ios_0f8d2573", "ui.nav.android_e4bbe5b7", "ui.nav.mobile_kw_live", "ui.nav.mobile_kw_map"],
          },
          {
            path: 'mobile/accounts', label: "ui.nav.app_accounts_39bc0afb", icon: UserCircle, built: true,
            keywords: ["ui.nav.patient_login_f07e3d0b", "ui.nav.members_f13eb585", "ui.nav.users_5b7dcd14", "ui.nav.mobile_kw_invite"],
          },
          {
            path: 'mobile/wearables', label: "ui.nav.mobile_wearables", icon: Watch, built: true,
            keywords: ["ui.nav.mobile_kw_ring", "ui.nav.mobile_kw_band", "ui.nav.mobile_kw_battery"],
          },
          {
            path: 'mobile/usage', label: "ui.nav.mobile_usage_analytics", icon: Activity, built: true,
            keywords: ["ui.nav.usage_0bb18642", "ui.nav.sessions_781791f5", "ui.nav.analytics_25bc9629", "ui.nav.mobile_kw_journey"],
          },
          {
            path: 'mobile/notifications', label: "ui.nav.mobile_notifications", icon: BellRing, built: true,
            keywords: ["ui.nav.mobile_kw_push", "ui.nav.mobile_kw_email", "ui.nav.mobile_kw_reminder"],
          },
        ],
      },
      {
        path: 'monad', label: "ui.nav.monad_702df4b7", icon: BrainCircuit,
        permission: 'MONAD_READ', built: false,
        keywords: ["ui.nav.ai_141a9241", "ui.nav.agent_0608c405", "ui.nav.model_1d06a0d7"],
      },
    ],
  },
  {
    section: "ui.nav.business_section",
    items: [
      {
        path: 'companies', label: "ui.nav.companies_9dba7ad7", icon: Building2,
        permission: 'COMPANY_READ', built: true,
        keywords: ["ui.nav.customer_b39f008e", "ui.nav.client_d2a04d71", "ui.nav.partner_3624db88"],
        children: [
          { path: 'companies/directory', label: "ui.nav.directory_4b892fe0", icon: List, built: true, keywords: ["ui.nav.acibadem_23e83e36", "ui.nav.silk_1f863fb6", "ui.nav.genobiome_a2bbf35c"] },
          { path: 'companies/catalogue', label: "ui.nav.catalogue_5a241023", icon: BookOpen, built: true, keywords: ["ui.nav.entitlement_d41a3be3", "ui.nav.may_order_fce2c4c8"] },
        ],
      },
      {
        path: 'organizations', label: "ui.nav.customers", icon: Building,
        permission: 'ORGANIZATION_READ', built: true,
        keywords: ["ui.nav.lab_org_clinic", "ui.nav.lab_org_hospital", "ui.nav.lab_org_sample_codes", "ui.nav.customer_kw_quote",
          "ui.nav.customer_kw_invoice"],
      },
      {
        path: 'customer-relations', label: "ui.nav.customer_relations", icon: Users,
        permission: null, built: false,
        keywords: ["ui.nav.customer_b39f008e", "ui.nav.email_a88b7dcd", "ui.nav.sms_83b04e26"],
        children: [
          {
            path: 'customer-relations/contacts', label: "ui.nav.contacts", icon: UserCircle,
            permission: 'MESSAGING_READ', built: false,
            keywords: ["ui.nav.customer_b39f008e", "ui.nav.email_a88b7dcd", "ui.nav.mailing_list"],
          },
          {
            path: 'messaging', label: "ui.nav.outgoing", icon: Send,
            permission: 'MESSAGING_READ', built: true,
            keywords: ["ui.nav.email_a88b7dcd", "ui.nav.sms_83b04e26", "ui.nav.delivery_391cb1e0"],
          },
        ],
      },
      {
        path: 'finance', label: "ui.nav.finance", icon: CircleDollarSign,
        permission: null, built: false,
        keywords: ["ui.nav.revenue", "ui.nav.payments", "ui.nav.refunds", "ui.nav.tax"],
        children: [
          {
            path: 'commerce/overview', label: "ui.nav.mobile_overview", icon: BarChart3,
            permission: 'COMMERCE_FINANCE_READ', built: true,
            keywords: ["ui.nav.revenue", "ui.nav.refunds", "ui.nav.tax"],
          },
          {
            path: 'transactions', label: "ui.nav.transactions", icon: CreditCard,
            permission: 'COMMERCE_FINANCE_READ', built: true,
            keywords: ["ui.nav.payments", "ui.nav.stripe", "ui.nav.refunds", "ui.nav.finance_kw_invoices", "ui.nav.finance_kw_shipping"],
          },
          // What customers still owe, and how late.
          {
            path: 'finance/receivables', label: "ui.nav.receivables", icon: Receipt,
            permission: 'COMMERCE_FINANCE_READ', built: true,
            keywords: ["ui.nav.finance_kw_invoices", "ui.nav.finance_kw_overdue"],
          },
          // What labels cost us, beside what orders were charged for shipping.
          {
            path: 'finance/costs', label: "ui.nav.shipping_costs", icon: Truck,
            permission: 'COMMERCE_FINANCE_READ', built: true,
            keywords: ["ui.nav.finance_kw_shipping", "ui.nav.finance_kw_labels"],
          },
          // The companies our invoices are issued by, each with its bank accounts.
          {
            path: 'finance/billing-profiles', label: "ui.nav.billing_profiles", icon: Landmark,
            permission: 'CUSTOMER_BILLING_MANAGE', built: true,
            keywords: ["ui.nav.finance_kw_invoices", "ui.nav.finance_kw_bank"],
          },
          {
            path: 'commerce/analytics', label: "ui.nav.analytics_25bc9629", icon: Activity,
            permission: 'COMMERCE_FINANCE_READ', built: true,
            keywords: ["ui.nav.revenue", "ui.nav.metrics_966b9734", "ui.nav.country_8e68b3e5"],
          },
        ],
      },
    ],
  },
  // The store in six lines, each one job. First how the store does at a glance
  // (the overview). Then what an office itself decides: which products it
  // sells, at what price, with how many, and what it asks for sending them.
  // Then what there is to sell (the catalogue), what takes money off a price
  // (discounts), what the website says (its pages, menu and policies), and
  // the rest of how the store does (analytics, as the tracker and the orders
  // tell it). The pages about visitors need
  // STOREFRONT_ANALYTICS_READ (which STANDARD and REVIEWER, holding
  // ANALYTICS_READ, do not have); creating campaigns and changing tracking are
  // checked by the server and shown only with their own codes.
  {
    section: "ui.nav.store_section",
    items: [
      {
        path: 'store/overview', label: "ui.nav.storefront_overview", icon: BarChart3, built: true,
        permission: 'STOREFRONT_ANALYTICS_READ',
        keywords: ["ui.nav.storefront_analytics", "ui.nav.analytics_25bc9629", "ui.nav.revenue", "ui.nav.storefront_kw_conversion"],
      },
      {
        // Who is on the site now, right under the overview.
        path: 'store/live', label: "ui.nav.storefront_live", icon: Radio, built: true,
        permission: 'STOREFRONT_ANALYTICS_READ',
        keywords: ["ui.nav.mobile_kw_live", "ui.nav.mobile_kw_map", "ui.nav.visits_ec7b4ab4", "ui.nav.basket_c9be014f"],
      },
      {
        // What there is to sell: names, pictures, pages, categories. The only
        // place a product or a category is added, so it comes before selling.
        path: 'catalogue', label: "ui.nav.catalogue_5a241023", icon: Boxes, built: true,
        permission: 'PRODUCT_READ',
        keywords: ["ui.nav.products_fe0a091f", "ui.nav.ecommerce", "ui.nav.catalogue_1b946f91"],
        children: [
          // What each product is: its name, pictures, page and options.
          { path: 'products', label: "ui.nav.product_information", icon: Boxes, built: true,
            keywords: ["ui.nav.products_fe0a091f", "ui.nav.ecommerce", "ui.nav.catalogue_1b946f91", "ui.nav.sku_cf559ae3", "ui.nav.report_type_b68b8e02"] },
          // A warehouse reads what each product is, nothing else of the catalogue.
          { path: 'categories', label: "ui.nav.categories", icon: Rows3, built: true, kinds: ['office', 'laboratory', 'retail'],
            keywords: ["ui.nav.ecommerce", "ui.nav.catalogue_1b946f91"] },
        ],
      },
      {
        // What is sold, for how much and how many there are: each office's own. Nothing is made here.
        path: 'store/selling', label: "ui.nav.selling", icon: Store, built: true,
        permission: 'PRODUCT_READ', kinds: ['office', 'laboratory'],
        keywords: ["ui.nav.products_fe0a091f", "ui.nav.ecommerce", "ui.nav.price", "ui.nav.stock"],
        children: [
          // The products: on sale or not, their prices, their stock.
          { path: 'store/products', label: "ui.nav.products_fe0a091f", icon: Tag, built: true,
            keywords: ["ui.nav.ecommerce", "ui.nav.on_sale", "ui.nav.price", "ui.nav.stock"] },
          // What it asks for sending an order, in every market.
          { path: 'store/shipping', label: "ui.nav.shipping_prices", icon: Truck, built: true,
            keywords: ["ui.nav.ecommerce", "ui.nav.price", "ui.nav.delivery_391cb1e0"] },
          // What it asked the warehouses for, and taking it in when it arrives.
          { path: 'store/stock-requests', label: "ui.nav.stock_requests", icon: PackagePlus, built: true,
            permission: 'STOCK_REQUEST_MANAGE',
            keywords: ["ui.nav.stock", "ui.nav.warehouse_section", "ui.nav.delivery_391cb1e0"] },
        ],
      },
      {
        path: 'discounts', label: "ui.nav.discounts", icon: TicketPercent, built: true,
        permission: 'PRODUCT_READ', kinds: ['office', 'laboratory'],
        keywords: ["ui.nav.ecommerce", "ui.nav.promotion", "ui.nav.discount"],
        children: [
          { path: 'coupons', label: "ui.nav.coupons", icon: TicketPercent, built: true,
            keywords: ["ui.nav.ecommerce", "ui.nav.promotion", "ui.nav.discount", "ui.nav.coupon_code"] },
          { path: 'bundles', label: "ui.nav.bundles", icon: Layers, built: true,
            keywords: ["ui.nav.ecommerce", "ui.nav.promotion", "ui.nav.discount", "ui.nav.bundle_together"] },
        ],
      },
      {
        // What the website says. Its words are site text, so these are read
        // and changed with the content permissions.
        path: 'website', label: "ui.nav.website", icon: PanelTop, built: true,
        permission: 'CONTENT_READ',
        keywords: ["ui.nav.content_4f9be057"],
        children: [
          // The names across the top of every page and their panels.
          { path: 'menu', label: "ui.nav.menu", icon: PanelTop, built: true,
            keywords: ["ui.nav.menu_navigation", "ui.nav.menu_links"] },
          { path: 'help-centre', label: "ui.nav.help_centre", icon: CircleHelp, built: true,
            keywords: ["ui.nav.help_centre_faq", "ui.nav.help_centre_questions"] },
          // Its guides and articles.
          { path: 'longevity-hub', label: "ui.nav.longevity_hub", icon: BookOpen, built: true,
            keywords: ["ui.nav.longevity_hub_articles", "ui.nav.longevity_hub_guides"] },
          // What it asks and recommends, and what visitors answered.
          { path: 'quiz', label: "ui.nav.quiz", icon: ClipboardCheck, built: true,
            keywords: ["ui.nav.quiz_questions", "ui.nav.quiz_answers"] },
          // Privacy, terms, refunds and shipping, as each office writes them for its own visitors.
          { path: 'policies', label: "ui.nav.policies", icon: FileText, built: true,
            keywords: ["ui.nav.privacy", "ui.nav.terms", "ui.nav.refunds"] },
        ],
      },
      {
        path: 'store/analytics', label: "ui.nav.analytics_25bc9629", icon: BarChart3, built: true,
        permission: 'STOREFRONT_ANALYTICS_READ',
        keywords: ["ui.nav.storefront_analytics", "ui.nav.metrics_966b9734"],
        children: [
          { path: 'store/campaigns', label: "ui.nav.storefront_campaigns", icon: Megaphone, built: true,
            keywords: ["ui.nav.storefront_kw_link", "ui.nav.storefront_kw_qr", "ui.nav.storefront_kw_utm", "ui.nav.storefront_kw_influencer"] },
          { path: 'store/sessions', label: "ui.nav.storefront_sessions", icon: PlayCircle, built: true,
            keywords: ["ui.nav.sessions_781791f5", "ui.nav.storefront_kw_replay", "ui.nav.mobile_kw_journey", "ui.nav.storefront_kw_timeline"] },
          { path: 'store/acquisition', label: "ui.nav.storefront_acquisition", icon: Compass, built: true,
            keywords: ["ui.nav.traffic_3d139ce0", "ui.nav.storefront_kw_channel", "ui.nav.storefront_kw_referrer", "ui.nav.landing_page"] },
          { path: 'store/audience', label: "ui.nav.storefront_audience", icon: Globe, built: true,
            keywords: ["ui.nav.country_8e68b3e5", "ui.nav.devices_728190e7", "ui.nav.storefront_kw_browser", "ui.nav.storefront_kw_language"] },
          { path: 'store/product-funnel', label: "ui.nav.storefront_product_funnel", icon: Filter, built: true,
            keywords: ["ui.nav.products_fe0a091f", "ui.nav.storefront_kw_funnel", "ui.nav.purchase_d376ca29"] },
          { path: 'store/checkout-funnel', label: "ui.nav.storefront_checkout_funnel", icon: CreditCard, built: true,
            keywords: ["ui.nav.checkout_d5491e7e", "ui.nav.payments", "ui.nav.coupon_code", "ui.nav.storefront_kw_abandoned"] },
          { path: 'store/pages', label: "ui.nav.storefront_pages", icon: MousePointerClick, built: true,
            keywords: ["ui.nav.storefront_kw_click_map", "ui.nav.storefront_kw_scroll", "ui.nav.landing_page"] },
          { path: 'store/performance', label: "ui.nav.storefront_performance", icon: Gauge, built: true,
            keywords: ["ui.nav.storefront_kw_web_vitals", "ui.nav.storefront_kw_errors", "ui.nav.storefront_kw_rage_clicks"] },
        ],
      },
    ],
  },
  // A warehouse: what it holds, and what the offices ask it for.
  {
    section: "ui.nav.warehouse_section",
    items: [
      {
        path: 'warehouse/stock', label: "ui.nav.stock", icon: Warehouse, built: true,
        permission: 'LOCATION_STOCK_MANAGE', kinds: ['warehouse'],
        keywords: ["ui.nav.products_fe0a091f", "ui.nav.warehouse_section"],
      },
      {
        path: 'warehouse/requests', label: "ui.nav.warehouse_requests", icon: Inbox, built: true,
        permission: 'LOCATION_STOCK_MANAGE', kinds: ['warehouse'],
        keywords: ["ui.nav.stock_requests", "ui.nav.parcel_58906f65", "ui.nav.courier_023daeb6"],
      },
    ],
  },
  // A retail shop: what it holds, and what it asked the warehouses for, as an office does.
  {
    section: "ui.nav.retail_section",
    items: [
      {
        path: 'retail/stock', label: "ui.nav.stock", icon: ShoppingBag, built: true,
        permission: 'LOCATION_STOCK_MANAGE', kinds: ['retail'],
        keywords: ["ui.nav.products_fe0a091f", "ui.nav.retail_section"],
      },
      {
        path: 'retail/requests', label: "ui.nav.stock_requests", icon: PackagePlus, built: true,
        permission: 'STOCK_REQUEST_MANAGE', kinds: ['retail'],
        keywords: ["ui.nav.stock", "ui.nav.warehouse_section", "ui.nav.delivery_391cb1e0"],
      },
    ],
  },
  {
    section: "ui.nav.marketing_section",
    items: [
      {
        path: 'marketing/analytics', label: "ui.nav.marketing_analytics", icon: BarChart3,
        permission: 'ANALYTICS_READ', built: false,
        keywords: ["ui.nav.analytics_25bc9629", "ui.nav.metrics_966b9734", "ui.nav.traffic_3d139ce0"],
      },
      {
        path: 'marketing/social', label: "ui.nav.social_media", icon: Send,
        permission: 'TRAFFIC_READ', built: false,
        keywords: ["ui.nav.marketing_section", "ui.nav.traffic_3d139ce0", "ui.nav.content_4f9be057"],
      },
    ],
  },
  {
    section: "ui.nav.infrastructure_951d9aa3",
    items: [
      {
        path: 'content', label: "ui.nav.content_4f9be057", icon: FileText,
        permission: 'CONTENT_READ', built: true,
        keywords: ["ui.nav.wording_8a77fd39", "ui.nav.copy_f84e2e2d", "ui.nav.text_372ea08c"],
        children: [
          { path: 'content/translations', label: "ui.nav.translations_8ad8302d", icon: Languages, built: true, keywords: ["ui.nav.locale_0e038a42", "ui.nav.string_ecb25204"] },
          { path: 'content/languages', label: "ui.nav.languages_db07be18", icon: Globe, built: true, keywords: ["ui.nav.locale_0e038a42"] },
        ],
      },
      {
        path: 'pipelines', label: "ui.nav.pipelines_c5df1e14", icon: Workflow,
        permission: 'PIPELINE_READ', built: true,
        keywords: ["ui.nav.workflow_899531e0", "ui.nav.nextflow_898aab62", "ui.nav.version_c692273d"],
        children: [
          { path: 'pipelines/catalogue', label: "ui.nav.catalogue_5a241023", icon: BookOpen, built: true, keywords: ["ui.nav.definitions_e3b26f06"] },
          { path: 'pipelines/releases', label: "ui.nav.releases_8fa41d59", icon: Package, built: true, keywords: ["ui.nav.tag_5e9b60f6", "ui.nav.rollout_85fdb6d2", "ui.nav.active_2bb6b986"] },
        ],
      },
      {
        path: 'cluster', label: "ui.nav.cluster_d75dc68b", icon: Server,
        permission: 'NODE_READ', built: true,
        keywords: ["ui.nav.nodes_00455006", "ui.nav.workers_f74e0903", "ui.nav.fleet_f35f940f", "ui.nav.capacity_7cb1f56d"],
      },
    ],
  },
  {
    section: "ui.nav.administration_b8be3d12",
    items: [
      {
        path: 'locations', label: "ui.nav.location_manager", icon: MapPin,
        permission: 'LOCATION_READ', built: true,
        keywords: ["ui.nav.office", "ui.nav.laboratory", "ui.nav.country_8e68b3e5", "ui.nav.responsibility"],
      },
      {
        path: 'devices', label: "ui.devices.title", icon: Printer,
        permission: 'KIT_READ', built: true,
        keywords: ["ui.devices.printers", "ui.devices.scanners", "ui.devices.wizard.wifi_title"],
      },
      {
        path: 'staff', label: "ui.nav.staff_a4730a22", icon: Users,
        permission: 'STAFF_READ', built: true,
        keywords: ["ui.nav.people_ab3ccc8d", "ui.nav.invite_f52bc44a", "ui.nav.accounts_10db2797", "ui.nav.roles_037c883f"],
      },
      {
        path: 'permissions', label: "ui.nav.permissions_d06d5557", icon: KeyRound,
        permission: 'ROLE_PERMISSIONS_MANAGE', built: true,
        keywords: ["ui.nav.roles_037c883f", "ui.nav.grants_41581c6e", "ui.nav.matrix_bf2f749e"],
      },
      {
        path: 'audit', label: "ui.nav.audit_log_3cfc5f1c", icon: ClipboardList,
        permission: 'AUDIT_READ', built: true,
        keywords: ["ui.nav.history_66f79d8a", "ui.nav.who_b7adf779", "ui.nav.compliance_036cb735", "ui.nav.trail_81a85c61"],
      },
      {
        path: 'feedback-inbox', label: "ui.nav.feedback_inbox", icon: Inbox,
        permission: 'FEEDBACK_READ', built: true,
        keywords: ["ui.nav.feedback_kw_bug", "ui.nav.feedback_kw_idea", "ui.nav.feedback_kw_report"],
      },
    ],
  },
  {
    section: null,
    items: [
      {
        path: 'feedback', label: "ui.nav.feedback", icon: MessageSquarePlus,
        permission: null, built: true,
        keywords: ["ui.nav.feedback_kw_bug", "ui.nav.feedback_kw_idea", "ui.nav.feedback_kw_report"],
      },
      {
        path: 'settings', label: "ui.nav.settings_c7f73bb5", icon: Settings,
        permission: null, built: true,
        keywords: ["ui.nav.notifications_1b60d2ed", "ui.nav.sessions_781791f5", "ui.nav.devices_728190e7", "ui.nav.preferences_dcedb31d"],
      },
    ],
  },
];

export const RECENTS_ICON = Clock;
export const SEARCH_ICON = Search;

function localizeItem(item) {
  return {
    ...item,
    label: tx(item.label),
    note: item.note ? tx(item.note) : item.note,
    keywords: item.keywords.map((keyword) => tx(keyword)),
    children: item.children?.map(localizeItem),
  };
}

/**
 * The tree this account may actually see.
 *
 * A child inherits its parent's permission rather than naming its own: the
 * parent is the thing being authorized, and a child that could outlive a hidden
 * parent would be reachable by URL but invisible in the menu. Items and then
 * sections drop out once empty, so nobody is shown a heading over nothing.
 *
 * `locationKind`: the kind of location the account works at, which decides
 * the items that name `kinds`. Not known, nothing is left out for it.
 */
export function visibleNav(permissions, locationKind = null) {
  const may = (permission) => permission === null || permissions.includes(permission);
  const here = (entry) => !entry.kinds || locationKind === null || permissions.includes('STORE_MANAGE_ALL')
    || entry.kinds.includes(locationKind);
  return NAV
    .map((group) => ({
      ...group,
      section: group.section === null ? null : tx(group.section),
      items: group.items.flatMap((item) => {
        if (!here(item)) return [];
        if (!item.children) return may(item.permission) ? [localizeItem(item)] : [];
        const children = item.children.filter((child) => here(child) && may(child.permission ?? item.permission));
        return children.length > 0 ? [localizeItem({ ...item, children })] : [];
      }),
    }))
    .filter((group) => group.items.length > 0);
}

/** Every reachable screen, flattened — what search looks through and what the
 *  router mounts. Children carry the section their parent sits in. */
export function destinationsOf(sections) {
  return sections.flatMap((group) => group.items.flatMap((item) => {
    const where = group.section === null ? tx('ui.nav.portal') : group.section;
    const self = { ...item, section: where, parent: null };
    if (!item.children) return [self];
    return [
      self,
      ...item.children.map((child) => ({
        ...child,
        section: where,
        parent: item.label,
        permission: child.permission ?? item.permission,
      })),
    ];
  }));
}
